import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/api';
import { Link } from 'react-router-dom';
import { MdOutlineBlock } from 'react-icons/md';
import Loader from '../Loader';
import Pagination from '../Pagination';
import { FaEye } from 'react-icons/fa';

const Users = () => {
  const [users, setUsers] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchUser, setSearchUser] = useState('');

  // Fetch All users
  const getUsers = useCallback(async () => {
    try {
      const response = await axios.get(`/entity?page_number=${currentPage}&page_size=${recordsPerPage}`);
      setUsers(response.data.Data);
      setTotal(response.data.Total);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setError(error);
      setLoading(false);
    }
  }, [currentPage, recordsPerPage]);

  // Search transaction
  const handleSearchChange = (e) => {
    setSearchUser(e.target.value);
    setCurrentPage(1);
  };

  // debounce for search query
  useEffect(() => {
    const timerId = setTimeout(() => {
      setLoading(true);
      getUsers(currentPage * recordsPerPage, recordsPerPage, searchUser);
    }, 500);
    return () => clearTimeout(timerId);
  }, [currentPage, recordsPerPage, searchUser, getUsers]);

  return (
    <div className='mx-auto p-4'>
      <div className='bg-white rounded-lg p-4 border border-[#e0e2e7] lg:w-[78vw] xl:w-[81vw] 2xl:w-full'>
        <div className='flex flex-wrap items-center justify-between py-3'>
          <Link to='/app/adduser' className='bg-[#fb0000] hover:bg-[#e85959] text-white px-5 py-2 rounded-lg'>Add User</Link>
          <h5 className='text-[#fb0000]'>Showing {users.length} users</h5>
          <div className='py-2'>
            <form>
              <label htmlFor='search'><span className='hidden'>Search</span>
                <input
                  type='search'
                  id='search'
                  className='px-3 py-2 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#fb0000] focus:ring-[#fb0000] focus:ring-1'
                  required
                  placeholder='Search user'
                  value={searchUser}
                  onChange={handleSearchChange}
                />
              </label>
            </form>
          </div>
        </div>
        {loading
          ? (
            <div className='flex items-center justify-center rounded-md bg-[#f1f4f6] h-screen md:h-[56em] lg:h-[25em] xl:h-[35em] 2xl:lg:h-[48em]'>
              <Loader />
            </div>
            )
          : error
            ? (
              <div className='bg-[#f1f4f6] rounded-md grid place-items-center h-screen md:h-[56em] lg:h-[25em] xl:h-[35em] 2xl:lg:h-[48em]'>
                <div className='grid place-items-center'>
                  <h2><MdOutlineBlock /></h2>
                  <h4>Error occurred while fetching data</h4>
                </div>
              </div>
              )
            : (
              <div className='overflow-x-auto rounded-md h-[73vh]'>
                {users.length > 0
                  ? (
                    <table className='w-full text-left table-auto'>
                      <thead>
                        <tr className='border-b border-slate-500'>
                          <th className='px-2 py-3'>First Name</th>
                          <th className='px-2 py-3'>Other Names</th>
                          <th className='px-2 py-3'>Email</th>
                          <th className='px-2 py-3'>Phone Number</th>
                          <th className='px-2 py-3'>ID Number</th>
                          <th className='px-2 py-3'>Role</th>
                          <th className='px-2 py-3'>Status</th>
                          <th className='px-2 py-3'>Created At</th>
                          <th className='px-2 py-3'>View Profile</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                          <tr key={user.id}>
                            <td className='p-2 '>{user.first_name}</td>
                            <td className='p-2'>{user.other_names}</td>
                            <td className='p-2'>{user.email}</td>
                            <td className='p-2'>{user.msisdn}</td>
                            <td className='p-2'>{user.id_number}</td>
                            <td className='p-2'>{user.role}</td>
                            <td className='p-2'>
                              {user.active_status === 'active'
                                ? <span className='bg-green-100 text-green-600 font-bold px-5 py-1 rounded-full'>Active</span>
                                : <span className='bg-red-100 text-red-600 font-bold px-5 py-1 rounded-full'>Inactive</span>}
                            </td>
                            <td className='p-2'>{new Date(user.start_date).toISOString().replace('T', ' ').slice(0, 19)}</td>
                            <td className='p-2'>
                              <Link to={`/app/user/${user.id}`}><span className='text-blue-600 text-xl hover:text-blue-400'><FaEye /></span></Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    )
                  : (
                    <div className='bg-[#f1f4f6] grid place-items-center h-screen md:h-[56em] lg:h-[25em] xl:h-[35em] 2xl:lg:h-[48em]'>
                      <div className='grid place-items-center'>
                        <h2><MdOutlineBlock /></h2>
                        <h4>No Data</h4>
                      </div>
                    </div>
                    )}
              </div>
              )}
        <div className='flex flex-wrap items-center justify-between mt-4 gap-4'>
          <div>
            <span className='mr-2'>Records per page:</span>
            <select
              className='px-3 py-1 border bg-[#f2f9ff] border-slate-300 rounded-md focus:outline-none focus:border-[#fb0000] focus:ring-[#fb0000]'
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
            >
              <option value='20'>20</option>
              <option value='50'>50</option>
              <option value='75'>75</option>
              <option value='100'>100</option>
            </select>
          </div>
          <Pagination
            nPages={Math.ceil(total / recordsPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default Users;
